import { type Static, Type } from "typebox";

import { IssueStatus, ServiceRecordState } from "./enums.js";
import { defineRoute } from "./route.js";
import {
  errors,
  Exact,
  Extend,
  Identifier,
  ListOf,
  Nullable,
  Page,
  Timestamp,
} from "./schema.js";

// --- Vendors ----------------------------------------------------------------

export const Vendor = Exact({
  address: Nullable(Type.String()),
  archivedAt: Nullable(Type.String()),
  email: Nullable(Type.String()),
  id: Identifier,
  name: Type.String(),
  notes: Nullable(Type.String()),
  phone: Nullable(Type.String()),
});
export type Vendor = Static<typeof Vendor>;

export const VendorList = ListOf(Vendor);
export type VendorList = Static<typeof VendorList>;

/** A vendor as it is stored, which is what the edits return. */
export const VendorRecord = Extend(Vendor, {
  archivedAt: Nullable(Timestamp),
  createdAt: Timestamp,
  organizationId: Identifier,
  updatedAt: Timestamp,
});
export type VendorRecord = Static<typeof VendorRecord>;

const VendorFields = {
  address: Nullable(Type.String({ maxLength: 1000 })),
  email: Nullable(Type.String({ maxLength: 320 })),
  name: Type.String({ maxLength: 160, minLength: 1 }),
  notes: Nullable(Type.String({ maxLength: 4000 })),
  phone: Nullable(Type.String({ maxLength: 64 })),
};

export const createVendor = defineRoute({
  body: Exact({
    address: Type.Optional(VendorFields.address),
    email: Type.Optional(VendorFields.email),
    name: VendorFields.name,
    notes: Type.Optional(VendorFields.notes),
    phone: Type.Optional(VendorFields.phone),
  }),
  method: "POST",
  path: "/organizations/:organizationId/vendors",
  response: { 201: VendorRecord, ...errors(401, 403, 422) },
  tags: ["vendors"],
});

export const listVendors = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/vendors",
  querystring: Exact({
    includeArchived: Type.Optional(Type.Boolean()),
    search: Type.Optional(Type.String({ maxLength: 160 })),
  }),
  response: { 200: VendorList, ...errors(401, 403) },
  tags: ["vendors"],
});

export const updateVendor = defineRoute({
  body: Exact({
    address: Type.Optional(VendorFields.address),
    email: Type.Optional(VendorFields.email),
    name: Type.Optional(VendorFields.name),
    notes: Type.Optional(VendorFields.notes),
    phone: Type.Optional(VendorFields.phone),
  }),
  method: "PATCH",
  path: "/organizations/:organizationId/vendors/:vendorId",
  response: { 200: VendorRecord, ...errors(401, 403, 404, 422) },
  tags: ["vendors"],
});

export const archiveVendor = defineRoute({
  method: "DELETE",
  path: "/organizations/:organizationId/vendors/:vendorId",
  response: { 200: VendorRecord, ...errors(401, 403, 404) },
  tags: ["vendors"],
});

// --- Service records --------------------------------------------------------

/**
 * What a list row and the detail page both show. The detail extends this,
 * so a field the list has cannot be missing from the detail.
 */
export const ServiceRecordSummary = Exact({
  /** Absent for roles that may not see what the fleet spends. */
  amountCents: Type.Optional(Type.Integer()),
  completedAt: Type.String(),
  completedOdometerMiles: Type.Integer(),
  currency: Type.Optional(Type.String()),
  id: Identifier,
  receiptCount: Type.Integer(),
  state: ServiceRecordState,
  taskNames: Type.Array(Type.String()),
  vehicleId: Identifier,
  vehicleLabel: Type.String(),
  vendorId: Nullable(Type.String()),
  vendorName: Nullable(Type.String()),
});
export type ServiceRecordSummary = Static<typeof ServiceRecordSummary>;

export const ServiceRecordPage = Page(ServiceRecordSummary);
export type ServiceRecordPage = Static<typeof ServiceRecordPage>;

export const ServiceTask = Exact({
  id: Identifier,
  maintenanceRuleId: Nullable(Type.String()),
  name: Type.String(),
  ruleName: Nullable(Type.String()),
});
export type ServiceTask = Static<typeof ServiceTask>;

export const ServiceReceipt = Exact({
  id: Identifier,
  mimeType: Type.String(),
  originalFilename: Type.String(),
  sizeBytes: Type.Integer(),
  /** Null when this deployment has no storage to sign a link from. */
  url: Nullable(Type.String()),
});
export type ServiceReceipt = Static<typeof ServiceReceipt>;

export const ServiceRecordDetail = Extend(ServiceRecordSummary, {
  // When corrected: who, when and why, and the version that replaced it.
  correctedAt: Nullable(Timestamp),
  correctedByName: Nullable(Type.String()),
  correctionReason: Nullable(Type.String()),
  correctsServiceRecordId: Nullable(Type.String()),
  createdAt: Timestamp,
  createdByName: Nullable(Type.String()),
  fixedIssues: Type.Array(
    Exact({ description: Type.String(), id: Identifier, status: IssueStatus }),
  ),
  notes: Nullable(Type.String()),
  receipts: Type.Array(ServiceReceipt),
  /** When the receipt links stop working, so the page can fetch fresh ones. */
  receiptUrlsExpireAt: Timestamp,
  replacementId: Nullable(Type.String()),
  tasks: Type.Array(ServiceTask),
});
export type ServiceRecordDetail = Static<typeof ServiceRecordDetail>;

const ServiceWork = {
  amountCents: Type.Integer({ minimum: 0 }),
  completedAt: Type.String({ format: "date-time" }),
  completedOdometerMiles: Type.Integer({ maximum: 10_000_000, minimum: 0 }),
  currency: Type.String({ pattern: "^[A-Z]{3}$" }),
  fileIds: Type.Optional(Type.Array(Identifier)),
  notes: Type.Optional(Type.String({ maxLength: 4_000 })),
  tasks: Type.Array(
    Exact({
      maintenanceRuleId: Type.Optional(Identifier),
      name: Type.String({ maxLength: 160, minLength: 1 }),
    }),
    { minItems: 1 },
  ),
  vehicleId: Identifier,
  vendorId: Type.Optional(Identifier),
};

export const createServiceRecord = defineRoute({
  body: Exact(ServiceWork),
  method: "POST",
  path: "/organizations/:organizationId/service-records",
  response: {
    201: Exact({ id: Identifier }),
    // 409 when the odometer does not fit between the vehicle's readings.
    ...errors(401, 403, 404, 409, 422),
  },
  tags: ["service-records"],
});

export const listServiceRecords = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/service-records",
  querystring: Exact({
    cursor: Type.Optional(Type.String({ maxLength: 400 })),
    from: Type.Optional(Type.String({ format: "date-time" })),
    limit: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
    task: Type.Optional(Type.String({ maxLength: 160 })),
    to: Type.Optional(Type.String({ format: "date-time" })),
    vehicleId: Type.Optional(Identifier),
    vendorId: Type.Optional(Identifier),
  }),
  response: { 200: ServiceRecordPage, ...errors(401, 403) },
  tags: ["service-records"],
});

export const getServiceRecord = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/service-records/:serviceRecordId",
  response: { 200: ServiceRecordDetail, ...errors(401, 403, 404) },
  tags: ["service-records"],
});

/** Details that affected nothing else, edited in place. */
export const updateServiceRecord = defineRoute({
  body: Exact(
    {
      amountCents: Type.Optional(Type.Integer({ minimum: 0 })),
      notes: Type.Optional(Nullable(Type.String({ maxLength: 4_000 }))),
      vendorId: Type.Optional(Nullable(Identifier)),
    },
    { minProperties: 1 },
  ),
  method: "PATCH",
  path: "/organizations/:organizationId/service-records/:serviceRecordId",
  response: {
    200: Exact({ id: Identifier }),
    ...errors(401, 403, 404, 422),
  },
  tags: ["service-records"],
});

/** Date, odometer or work was wrong: replace the record and its effects. */
export const correctServiceRecord = defineRoute({
  body: Exact({
    ...ServiceWork,
    reason: Type.String({ maxLength: 1_000, minLength: 1 }),
  }),
  method: "POST",
  path: "/organizations/:organizationId/service-records/:serviceRecordId/corrections",
  response: {
    201: Exact({ id: Identifier }),
    ...errors(401, 403, 404, 409, 422),
  },
  tags: ["service-records"],
});
